import { Component, Inject, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuditService } from '../../services/audit.service';
import { PageProcessService } from '../../services/page-process.service';
import { AuditResult, Vulnerability } from '../../models';

@Component({
  selector: 'app-audit-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTabsModule,
    MatProgressBarModule,
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>assessment</mat-icon>
      Historial de Auditorías
    </h2>

    <div mat-dialog-content class="dialog-content">
      <div class="page-info" *ngIf="data.pageName">
        <span class="label">Página:</span>
        <span class="value">{{ data.pageName }}</span>
      </div>

      <div class="stats-container" *ngIf="audits.length > 0">
        <mat-card class="stat-card">
          <div class="stat-label">Total Auditorías</div>
          <div class="stat-value">{{ audits.length }}</div>
        </mat-card>

        <mat-card class="stat-card">
          <div class="stat-label">Score Promedio</div>
          <div class="stat-value">{{ averageScore | number: '1.0-0' }}%</div>
        </mat-card>

        <mat-card class="stat-card">
          <div class="stat-label">Último Score</div>
          <div class="stat-value" [ngClass]="getScoreClass(lastScore)">{{ lastScore }}%</div>
        </mat-card>
      </div>

      <div *ngIf="loading" class="loading-container">
        <mat-icon class="loading-icon">hourglass_empty</mat-icon>
        <span>Cargando auditorías...</span>
      </div>

      <div *ngIf="!loading && audits.length === 0" class="empty-state">
        <mat-icon class="empty-icon">assignment</mat-icon>
        <span>No hay auditorías registradas para esta página</span>
      </div>

      <mat-card class="audits-card" *ngIf="!loading && audits.length > 0">
        <mat-accordion class="audits-accordion">
          <mat-expansion-panel *ngFor="let audit of audits; trackBy: trackByAuditId">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <div class="audit-header">
                  <span class="audit-date">{{ audit.date | date: 'dd/MM/yyyy' }}</span>
                  <span class="audit-time">{{ audit.date | date: 'HH:mm' }}</span>
                </div>
              </mat-panel-title>
              <mat-panel-description>
                <div class="audit-score-badge" [ngClass]="getScoreClass(audit.score)">
                  {{ audit.score }}%
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="audit-details">
              <div class="vulnerabilities-summary">
                <h4>Vulnerabilidades Detectadas</h4>
                <div class="vuln-badges">
                  <span
                    class="vuln-badge critical"
                    *ngIf="getVulnCount(audit, 'critical') > 0"
                    matTooltip="Críticas"
                  >
                    <mat-icon>warning</mat-icon>
                    {{ getVulnCount(audit, 'critical') }} Críticas
                  </span>
                  <span
                    class="vuln-badge high"
                    *ngIf="getVulnCount(audit, 'high') > 0"
                    matTooltip="Altas"
                  >
                    <mat-icon>error</mat-icon>
                    {{ getVulnCount(audit, 'high') }} Altas
                  </span>
                  <span
                    class="vuln-badge medium"
                    *ngIf="getVulnCount(audit, 'medium') > 0"
                    matTooltip="Medias"
                  >
                    <mat-icon>info</mat-icon>
                    {{ getVulnCount(audit, 'medium') }} Medias
                  </span>
                  <span
                    class="vuln-badge low"
                    *ngIf="getVulnCount(audit, 'low') > 0"
                    matTooltip="Bajas"
                  >
                    <mat-icon>check_circle</mat-icon>
                    {{ getVulnCount(audit, 'low') }} Bajas
                  </span>
                  <span class="vuln-badge none" *ngIf="audit.vulnerabilities.length === 0">
                    <mat-icon>verified</mat-icon>
                    Sin vulnerabilidades
                  </span>
                </div>
              </div>

              <table mat-table [dataSource]="audit.vulnerabilities" class="vuln-table" *ngIf="audit.vulnerabilities.length > 0">
                <ng-container matColumnDef="type">
                  <th mat-header-cell *matHeaderCellDef>Tipo</th>
                  <td mat-cell *matCellDef="let vuln">{{ vuln.type }}</td>
                </ng-container>

                <ng-container matColumnDef="severity">
                  <th mat-header-cell *matHeaderCellDef>Severidad</th>
                  <td mat-cell *matCellDef="let vuln">
                    <span class="severity-badge" [ngClass]="vuln.severity">
                      {{ getSeverityLabel(vuln.severity) }}
                    </span>
                  </td>
                </ng-container>

                <ng-container matColumnDef="description">
                  <th mat-header-cell *matHeaderCellDef>Descripción</th>
                  <td mat-cell *matCellDef="let vuln">{{ vuln.description }}</td>
                </ng-container>

                <ng-container matColumnDef="recommendation">
                  <th mat-header-cell *matHeaderCellDef>Recomendación</th>
                  <td mat-cell *matCellDef="let vuln">{{ vuln.recommendation }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="vulnColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: vulnColumns"></tr>
              </table>
              <div class="report-actions" *ngIf="audit.status === 'completed' && audit.processId">
                <button mat-raised-button color="primary" (click)="viewReport(audit)">
                  <mat-icon>picture_as_pdf</mat-icon>
                  Ver Reporte PDF
                </button>
              </div>
            </div>
          </mat-expansion-panel>
        </mat-accordion>
      </mat-card>
    </div>

    <mat-dialog-actions align="end">
      <button mat-button (click)="closeDialog()">Cerrar</button>
    </mat-dialog-actions>
  `,
  styles: [`
     .dialog-content {
       max-width: 900px;
       width: min(900px, 92vw);
       overflow: visible;
     }

    h2 {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0;
      font-family: var(--font-display);
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .page-info {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 20px;
      padding: 12px;
      background: #f7f8fa;
      border-radius: var(--radius-sm);

      .label {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.6);
      }

      .value {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
      }
    }

    .stats-container {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 20px;
    }

    .stat-card {
      text-align: center;
      padding: 16px;
      border-radius: var(--radius-lg);
      border: 1px solid rgba(0, 0, 0, 0.05);
      box-shadow: var(--shadow-sm);

      .stat-label {
        font-size: 13px;
        color: rgba(0, 0, 0, 0.6);
        margin-bottom: 8px;
      }

      .stat-value {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: rgba(0, 0, 0, 0.87);

        &.high {
          color: #2e7d32;
        }

        &.medium {
          color: #f9a825;
        }

        &.low {
          color: #c62828;
        }
      }
    }

    .loading-container,
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      gap: 16px;
      color: rgba(0, 0, 0, 0.54);

      .loading-icon {
        animation: spin 1s linear infinite;
        font-size: 48px;
      }

      .empty-icon {
        font-size: 64px;
        color: rgba(0, 0, 0, 0.38);
      }

      span {
        font-size: 14px;
      }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .audits-card {
      margin-top: 0;
      border-radius: var(--radius-lg);
      border: 1px solid rgba(0, 0, 0, 0.05);
      box-shadow: var(--shadow-sm);
    }

    .audits-accordion {
      box-shadow: none;
    }

    .audit-header {
      display: flex;
      align-items: center;
      gap: 12px;

      .audit-date {
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
      }

      .audit-time {
        font-size: 13px;
        color: rgba(0, 0, 0, 0.54);
      }
    }

    .audit-score-badge {
      padding: 4px 12px;
      border-radius: 999px;
      font-weight: 600;
      font-size: 13px;

      &.high {
        background: rgba(46, 125, 50, 0.1);
        color: #2e7d32;
      }

      &.medium {
        background: rgba(249, 168, 37, 0.1);
        color: #f9a825;
      }

      &.low {
        background: rgba(198, 40, 40, 0.1);
        color: #c62828;
      }
    }

    .audit-details {
      padding: 0 16px 16px 16px;
    }

    .report-actions {
      margin-top: 16px;
      display: flex;
      justify-content: flex-end;

      button {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
    }

    .vulnerabilities-summary {
      margin-bottom: 20px;

      h4 {
        margin: 0 0 12px 0;
        font-size: 14px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.87);
      }

      .vuln-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
    }

    .vuln-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;

      mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }

      &.critical {
        background: rgba(183, 28, 28, 0.1);
        color: #b71c1c;
      }

      &.high {
        background: rgba(230, 81, 0, 0.1);
        color: #e65100;
      }

      &.medium {
        background: rgba(249, 168, 37, 0.1);
        color: #f9a825;
      }

      &.low {
        background: rgba(46, 125, 50, 0.1);
        color: #2e7d32;
      }

      &.none {
        background: rgba(46, 125, 50, 0.1);
        color: #2e7d32;
      }
    }

    .vuln-table {
      width: 100%;
      margin-top: 16px;

      ::ng-deep .mat-mdc-header-cell {
        background: #f7f8fa;
      }
    }

    .severity-badge {
      padding: 4px 8px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 600;

      &.critical {
        background: rgba(183, 28, 28, 0.1);
        color: #b71c1c;
      }

      &.high {
        background: rgba(230, 81, 0, 0.1);
        color: #e65100;
      }

      &.medium {
        background: rgba(249, 168, 37, 0.1);
        color: #f9a825;
      }

      &.low {
        background: rgba(46, 125, 50, 0.1);
        color: #2e7d32;
      }
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
    }
  `]
})
export class AuditHistoryDialogComponent implements OnInit {
  audits: AuditResult[] = [];
  loading = true;
  vulnColumns = ['type', 'severity', 'description', 'recommendation'];
  auditService = inject(AuditService);
  pageProcessService = inject(PageProcessService);
  cdr = inject(ChangeDetectorRef);

  averageScore = 0;
  lastScore = 0;

  constructor(
    public dialogRef: MatDialogRef<AuditHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { pageId: number; pageName: string }
  ) {
  }

  ngOnInit() {
    this.loadAudits();
  }

  loadAudits() {
    if (!this.data.pageId) {
      console.error('Page ID is missing');
      this.loading = false;
      this.cdr.markForCheck();
      return;
    }

    this.auditService.getAuditsByPage(this.data.pageId).subscribe({
      next: (audits) => {
        this.audits = audits.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        this.calculateStats();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading audits:', error);
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  viewReport(audit: AuditResult) {
    if (!audit.processId) {
      return;
    }
    this.pageProcessService.getAuditReportBlob(audit.processId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      },
      error: () => {
        console.error('Error al cargar el reporte PDF');
      }
    });
  }

  calculateStats() {
    if (this.audits.length === 0) {
      this.averageScore = 0;
      this.lastScore = 0;
      return;
    }

    const sum = this.audits.reduce((acc, audit) => acc + audit.score, 0);
    this.averageScore = sum / this.audits.length;
    this.lastScore = this.audits[0].score;
  }

  getVulnCount(audit: AuditResult, severity: string): number {
    return audit.vulnerabilities.filter(v => v.severity === severity).length;
  }

  getSeverityLabel(severity: string): string {
    const labels: Record<string, string> = {
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Media',
      low: 'Baja'
    };
    return labels[severity] || severity;
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  }

  trackByAuditId(index: number, audit: AuditResult): number {
    return audit.id;
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
